import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { AutoComplete } from 'primereact/autocomplete';
import React, { useEffect, useState } from 'react';
import residentCardsService from '../../service/api/ResidentCardsService';
import suggestionsService from '../../service/api/SuggestionsService';
import FormErrorMessage from '../_shared/FormErrorMessage';
import FormErrorMessageScroller from '../_shared/FormErrorMessageScroller';
import { useTranslation } from 'react-i18next';
import { ResidentCard } from '../../types/ResidentCard';
import { SuggestionOutput } from '../../types/Suggestion';

interface FormValuesProps extends ResidentCard {}
const INIT_FORM_STATE: FormValuesProps = {
  id: null,
  number: '',
  propertyId: null,
};

type Props = {
  dialogVisible: boolean;
  closeAddEditModal: VoidFunction;
  residentCard: ResidentCard;
};

function ResidentCardDialog({ dialogVisible, closeAddEditModal, residentCard }: Props) {
  const { t } = useTranslation();

  const [initFormValues, setInitFormValues] = useState(INIT_FORM_STATE);

  // The property is picked via the suggestion API instead of loading all
  // properties up front. `selectedProperty` holds the currently displayed
  // suggestion ({ id, value }); formik keeps `propertyId` as the source of truth.
  const [selectedProperty, setSelectedProperty] = useState<SuggestionOutput>(null);
  const [propertySuggestions, setPropertySuggestions] = useState<SuggestionOutput[]>([]);

  const hideDialog = () => {
    formik.resetForm({ values: INIT_FORM_STATE });
    setSelectedProperty(null);
    closeAddEditModal();
  };

  useEffect(() => {
    if (dialogVisible && residentCard) {
      setInitFormValues({
        id: residentCard.id,
        number: residentCard.number,
        propertyId: residentCard.propertyId,
      });
      // Initialize the autocomplete display from the row's property address.
      setSelectedProperty(
        residentCard.propertyId
          ? { id: residentCard.propertyId, value: residentCard.propertyName ?? '' }
          : null
      );
    } else {
      setInitFormValues(INIT_FORM_STATE);
      setSelectedProperty(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible]);

  const searchProperties = async (event: { query: string }) => {
    const results = await suggestionsService.getSuggestions({
      query: event.query,
      entity: 'properties',
    });
    setPropertySuggestions(results);
  };

  const onPropertyChange = (value: SuggestionOutput | string) => {
    if (value && typeof value === 'object') {
      setSelectedProperty(value);
      formik.setFieldValue('propertyId', value.id);
    } else {
      // free text typed but not selected yet
      setSelectedProperty(value as any);
      formik.setFieldValue('propertyId', null);
    }
  };

  const formik = useFormik({
    initialValues: initFormValues,
    validate: (data: FormValuesProps) => {
      const errors: FormikErrors<FormValuesProps> = {};

      if (!data.number?.trim()) {
        errors.number = t('residentCards.error_number_required');
      }
      if (!data.propertyId) {
        errors.propertyId = t('residentCards.error_property_required');
      }
      return errors;
    },
    onSubmit: async (formData: FormValuesProps, helpers: FormikHelpers<FormValuesProps>) => {
      const onConflict = (error) => {
        if (error?.response?.status === 409) {
          helpers.setFieldError('number', t('residentCards.error_number_already_taken'));
        }
      };

      if (!formData.id) {
        residentCardsService
          .create(formData)
          .then(() => hideDialog())
          .catch(onConflict)
          .finally(() => helpers.setSubmitting(false));
      } else {
        residentCardsService
          .edit(formData, formData.id)
          .then(() => hideDialog())
          .catch(onConflict)
          .finally(() => helpers.setSubmitting(false));
      }
    },
    enableReinitialize: true,
  });

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <Button
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={formik.submitForm}
      />
    </>
  );

  return (
    <FormErrorMessageScroller formikInstance={formik}>
      <Dialog
        visible={dialogVisible}
        header={t('residentCards.resident_card_details')}
        modal
        className="p-fluid"
        footer={DialogFooter}
        onHide={hideDialog}
        breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
        style={{ width: '40vw' }}
      >
        <form>
          <div className="col-10">
            <div className="field">
              <label htmlFor="number">{t('residentCards.number')}</label>
              <InputText
                id="number"
                value={formik.values.number}
                onChange={formik.handleChange}
                className={formik.touched.number && formik.errors.number && 'p-invalid'}
              />
              <FormErrorMessage fieldName="number" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="propertyId">{t('residentCards.property')}</label>
              <AutoComplete
                id="propertyId"
                value={selectedProperty}
                suggestions={propertySuggestions}
                completeMethod={searchProperties}
                field="value"
                delay={300}
                dropdown
                placeholder={t('common.select')}
                onChange={(e) => onPropertyChange(e.value)}
                className={formik.touched.propertyId && formik.errors.propertyId && 'p-invalid'}
              />
              <FormErrorMessage fieldName="propertyId" formikInstance={formik} />
            </div>
          </div>
        </form>
      </Dialog>
    </FormErrorMessageScroller>
  );
}

export default ResidentCardDialog;
